import React, { useEffect, useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { FieldLabel, inputClass } from '../../create/shared/FieldLabel';
import { useReferralSettings, useSettingsMutations } from '../api/useReferralSettings';
import { COINS_MAX, UpdateSettingsBody } from '../api/types';
import { formatCount, formatDateTime } from '../format';
import { CampaignStatusBadge, FallbackDealBadge } from './controls';

/**
 * Referral settings (SSA-1031) — the coin-amount half of SSA-954's settings
 * endpoint, which SSA-955 shipped without a UI.
 *
 * The amounts live in AppConfig, not Postgres, so a save takes effect on the
 * consumer award Lambdas without a redeploy (SSA-1023). The panel reports
 * `coins_source` because a value reading 10 could mean "an admin chose 10" or
 * "nobody has ever set this" — and only the first survives a stack deploy on
 * dev/staging, where the AppConfig document is re-rendered from the SAM template.
 *
 * The fallback deal itself is shown read-only. The settings endpoint can write
 * its `name` and `deal_description`, but the campaign builder owns those along
 * with the window, cap, eligible businesses and status; two editors for one
 * field is how they drift.
 */

function parseCoins(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return Number.isInteger(n) && n >= 0 && n <= COINS_MAX ? n : null;
}

function CoinField({
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <FieldLabel required>{label}</FieldLabel>
      <input
        type="number"
        min={0}
        max={COINS_MAX}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    </div>
  );
}

function DependentCounts({
  explicit,
  implicit,
  total,
}: {
  explicit: number;
  implicit: number;
  total: number;
}) {
  return (
    <div className="border border-gray-800 rounded-xl p-3.5 bg-gray-900/40">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Codes riding on the fallback deal
      </p>
      <p className="mt-2 text-2xl font-semibold text-white tabular-nums">{formatCount(total)}</p>
      <p className="mt-1 text-xs text-gray-500">
        {formatCount(explicit)} bound to it explicitly · {formatCount(implicit)} resolved at grant
        time
      </p>
      {total > 0 && (
        <p className="mt-2 text-xs text-amber-300">
          Editing the fallback deal changes what all {formatCount(total)} of these grant.
        </p>
      )}
    </div>
  );
}

export function SettingsPanel({ env, refreshKey = 0 }: { env: Env; refreshKey?: number }) {
  const { data, loading, error: loadError } = useReferralSettings(env, refreshKey);
  const { update, saving, error: saveError, setError: setSaveError } = useSettingsMutations(env);

  const [signup, setSignup] = useState('');
  const [welcome, setWelcome] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  /**
   * What we believe is stored, which is NOT always what a re-read returns.
   *
   * This endpoint reads the coin amounts through the AppConfig Lambda extension,
   * whose sidecar cache refreshes on a poll interval — so for up to about a
   * minute after a write, a fresh GET still serves the PREVIOUS amounts. Seeding
   * the inputs from such a read would silently revert the number the operator
   * just saved and make a successful write look like it failed.
   *
   * So the baseline moves to the submitted values on a successful save, and we
   * deliberately do not re-read afterwards. The PUT is authoritative: it wrote
   * the hosted version and returned its number.
   */
  const [baseline, setBaseline] = useState<{ signup: number; welcome: number } | null>(null);
  /** A save proves the keys are in AppConfig, even if a stale read still says 'default'. */
  const [savedToAppConfig, setSavedToAppConfig] = useState(false);

  // Seed from the server on first load and whenever the environment changes.
  useEffect(() => {
    if (!data) return;
    setSignup(String(data.coins.signup));
    setWelcome(String(data.coins.welcome));
    setBaseline({ signup: data.coins.signup, welcome: data.coins.welcome });
    setFormError(null);
  }, [data]);

  useEffect(() => {
    setSavedToAppConfig(false);
    setFlash(null);
  }, [env]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <ErrorBanner message={loadError} />
        {loadError.includes('feature_disabled') && (
          <p className="text-xs text-gray-500">
            `referral_codes_enabled` is off for {env}. The settings endpoint is gated on it, so this
            panel stays unavailable until the flag is on.
          </p>
        )}
      </div>
    );
  }

  if (!data) return null;

  const fallback = data.personal_default;
  const parsedSignup = parseCoins(signup);
  const parsedWelcome = parseCoins(welcome);
  const current = baseline ?? data.coins;
  const dirty = parsedSignup !== current.signup || parsedWelcome !== current.welcome;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFlash(null);
    setSaveError(null);

    if (parsedSignup === null || parsedWelcome === null) {
      setFormError(`Both amounts must be whole numbers between 0 and ${COINS_MAX.toLocaleString()}.`);
      return;
    }
    setFormError(null);

    // Send only what changed — the endpoint rejects a body with no editable
    // field, and an unchanged amount would churn an AppConfig version for nothing.
    const body: UpdateSettingsBody = {};
    if (parsedSignup !== current.signup) body.coins_signup = parsedSignup;
    if (parsedWelcome !== current.welcome) body.coins_welcome = parsedWelcome;
    if (Object.keys(body).length === 0) {
      setFormError('Nothing to save — both amounts already match what is configured.');
      return;
    }

    const saved = await update(body);
    if (!saved) return;

    // Advance the baseline to what we just wrote rather than re-reading: a GET
    // now would come back through a stale extension cache and revert the field.
    setBaseline({ signup: parsedSignup, welcome: parsedWelcome });
    setSavedToAppConfig(true);
    setFlash(
      saved.coins?.changed
        ? `Saved as AppConfig version ${saved.coins.config_version}. Reads can lag about a minute behind while caches refresh — the stored value is already the new one.`
        : 'Saved. The amounts already matched, so no new AppConfig version was created.',
    );
  };

  return (
    <div className="space-y-5">
      <form onSubmit={onSubmit} className="space-y-5">
        <div className="border border-gray-800 rounded-xl p-4 bg-gray-900/40 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold text-white">Referral coin amounts</h3>
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-md border ${
                data.coins_source === 'appconfig' || savedToAppConfig
                  ? 'bg-violet-950/40 border-violet-700/60 text-violet-300'
                  : 'bg-gray-900 border-gray-700 text-gray-400'
              }`}
            >
              {data.coins_source === 'appconfig' || savedToAppConfig
                ? 'Set in AppConfig'
                : 'Using defaults'}
            </span>
          </div>

          <p className="text-xs text-gray-500">
            Stored in AppConfig under <code className="text-gray-400">{data.coin_keys.signup}</code>{' '}
            and <code className="text-gray-400">{data.coin_keys.welcome}</code>. A save takes effect
            without a deploy.
          </p>
          <p className="text-xs text-gray-500">
            These are read through a per-container cache, so shortly after a save a reload can still
            show the previous amount — and two reloads can disagree. A save is applied the moment it
            succeeds; the displayed value catches up.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <CoinField
              label="Referrer coins (signup)"
              hint="Paid to the owner of a personal code when someone signs up with it."
              value={signup}
              onChange={setSignup}
              disabled={saving}
            />
            <CoinField
              label="Welcome coins"
              hint="Paid to the new user, for every code kind."
              value={welcome}
              onChange={setWelcome}
              disabled={saving}
            />
          </div>

          {formError && <ErrorBanner message={formError} />}
          {saveError && <ErrorBanner message={saveError} />}
          {flash && (
            <div className="px-3 py-2 bg-emerald-950/30 border border-emerald-900/50 rounded-lg text-xs text-emerald-300">
              {flash}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving || !dirty}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save amounts'}
            </button>
            {saving && <Spinner size="sm" />}
          </div>
        </div>
      </form>

      <DependentCounts
        explicit={data.dependent_active_codes.explicit}
        implicit={data.dependent_active_codes.implicit}
        total={data.dependent_active_codes.total}
      />

      <div className="border border-gray-800 rounded-xl p-4 bg-gray-900/40 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-white">Fallback deal</h3>
          <FallbackDealBadge />
          {fallback && <CampaignStatusBadge status={fallback.status} />}
        </div>

        {fallback ? (
          <>
            <p className="text-sm text-gray-300">{fallback.deal_description}</p>
            <p className="text-xs text-gray-500">
              {fallback.name} · {fallback.eligible_businesses.length} eligible{' '}
              {fallback.eligible_businesses.length === 1 ? 'business' : 'businesses'} ·{' '}
              {fallback.max_claims == null
                ? 'uncapped'
                : `${formatCount(fallback.claim_count)} / ${formatCount(fallback.max_claims)} claimed`}
            </p>
            <p className="text-xs text-gray-500">
              Runs {formatDateTime(fallback.starts_at)} → {formatDateTime(fallback.ends_at)}
            </p>
            <p className="text-xs text-gray-500 pt-1">
              Edit this under <b className="text-gray-400">Deal campaigns</b> — the builder owns its
              text, window, cap, eligible businesses and status.
            </p>
          </>
        ) : (
          <p className="text-xs text-gray-500">
            No fallback campaign exists in this environment yet.
          </p>
        )}
      </div>
    </div>
  );
}
